import React, { useState, useMemo, memo } from 'react';
import PropTypes from 'prop-types';

const NeumorphicButtonShort = ({
  children,
  type = 'button',
  onClick,
  disabled,
  isLoading,
  className = '',
  textColor = 'white',
  hoverColor = '#9404C3',
  style = {}
}) => {
  // Generate unique IDs for filters and gradients using useMemo
  const filterIds = useMemo(() => ({
    filterId: `button_filter_${Math.random().toString(36).substr(2, 9)}`,
    gradientId: `button_gradient_${Math.random().toString(36).substr(2, 9)}`,
    hoverGradientId: `hover_gradient_${Math.random().toString(36).substr(2, 9)}`
  }), []);

  const { filterId, gradientId, hoverGradientId } = filterIds;

  // State for hover
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`neumorphic-button-container ${className}`} 
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: '88px', // Increased spacing between buttons
        ...style
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <svg 
        style={{ 
          position: 'absolute', 
          width: '120%', 
          height: '100%', 
          top: 0, 
          left: '-10%',
          transition: 'all 0.3s ease'
        }}
        viewBox="0 0 281 113" 
        fill="none"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Normal gradient */}
          <linearGradient id={gradientId} x1="30" y1="30" x2="39.9945" y2="124.948" gradientUnits="userSpaceOnUse">
            <stop offset="1" stopColor="#2A2D35"/>
            <stop stopColor="#262930"/>
          </linearGradient>
          
          {/* Hover gradient */}
          <linearGradient id={hoverGradientId} x1="30" y1="54" x2="246" y2="54" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#9404C3"/>
            <stop offset="1" stopColor="#47025D"/>
          </linearGradient>
          
          <filter id={filterId} x="0" y="0" width="281" height="113" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="10"/>
            <feGaussianBlur stdDeviation="12.5"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_570_582"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-10" dy="-10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.258824 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="effect1_dropShadow_570_582" result="effect2_dropShadow_570_582"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="-10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect2_dropShadow_570_582" result="effect3_dropShadow_570_582"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-10" dy="10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect3_dropShadow_570_582" result="effect4_dropShadow_570_582"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect4_dropShadow_570_582" result="shape"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-1" dy="-1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.5 0"/>
            <feBlend mode="normal" in2="shape" result="effect5_innerShadow_570_582"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="1" dy="1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.258824 0 0 0 0.3 0"/>
            <feBlend mode="normal" in2="effect5_innerShadow_570_582" result="effect6_innerShadow_570_582"/>
          </filter>
          
          {/* Hover state filter */}
          <filter id={`${filterId}_hover`} x="0" y="0" width="281" height="113" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha1"/>
            <feOffset dx="10" dy="10"/>
            <feGaussianBlur stdDeviation="8.33"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="SourceGraphic" result="effect1_dropShadow"/>
            
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha2"/>
            <feOffset dx="-10" dy="-10"/>
            <feGaussianBlur stdDeviation="6.67"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.258824 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow"/>
            
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha3"/>
            <feOffset dx="10" dy="-10"/>
            <feGaussianBlur stdDeviation="6.67"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect2_dropShadow" result="effect3_dropShadow"/>
            
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha4"/>
            <feOffset dx="-10" dy="10"/>
            <feGaussianBlur stdDeviation="6.67"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect3_dropShadow" result="effect4_dropShadow"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect4_dropShadow" result="shape"/>
            
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha5"/>
            <feOffset dx="1" dy="1"/>
            <feGaussianBlur stdDeviation="0.67"/>
            <feComposite in2="hardAlpha5" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.258824 0 0 0 0.3 0"/>
            <feBlend mode="normal" in2="shape" result="effect5_innerShadow"/>
            
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha6"/>
            <feOffset dx="-1" dy="-1"/>
            <feGaussianBlur stdDeviation="0.67"/>
            <feComposite in2="hardAlpha6" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.5 0"/>
            <feBlend mode="normal" in2="effect5_innerShadow" result="effect6_innerShadow"/>
          </filter>
        </defs>
        
        <g filter={isHovered ? `url(#${filterId}_hover)` : `url(#${filterId})`}>
          <path 
            d="M41.1362 76.4884L30.3953 57.6917C30.1362 57.2384 30 56.7254 30 56.2033V33C30 31.3431 31.3431 30 33 30H232.259C233.336 30 234.33 30.5769 234.864 31.5116L245.605 50.3083C245.864 50.7616 246 51.2746 246 51.7967V75C246 76.6569 244.657 78 243 78H43.741C42.6644 78 41.6704 77.4231 41.1362 76.4884Z" 
            fill={isHovered ? `url(#${hoverGradientId})` : `url(#${gradientId})`}
          />
        </g>
      </svg>
      
      {/* Actual button */}
      <button
        type={type}
        disabled={disabled || isLoading}
        onClick={onClick}
        className={`neumorphic-button ${isLoading ? 'neumorphic-button-loading' : ''}`}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          color: isHovered ? 'white' : textColor,
          border: 'none',
          outline: 'none',
          cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
          zIndex: 2,
          opacity: disabled ? 0.7 : 1,
          transition: 'all 0.2s ease',
          padding: 0 // Remove default padding
        }}
      >
        {isLoading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        ) : (
          children
        )}
      </button>
      
      {/* CSS for the loading spinner */}
      <style>
        {`
          .neumorphic-button-loading {
            color: transparent !important;
            pointer-events: none;
          }
          
          .spinner-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .spinner {
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 50%;
            border: 2px solid rgba(255, 255, 255, 0.2);
            border-top-color: white;
            animation: spinner-rotation 0.8s linear infinite;
          }
          
          @keyframes spinner-rotation {
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
};

const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.children === nextProps.children &&
    prevProps.type === nextProps.type &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.className === nextProps.className &&
    prevProps.textColor === nextProps.textColor &&
    prevProps.hoverColor === nextProps.hoverColor &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style)
  );
};

NeumorphicButtonShort.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  textColor: PropTypes.string,
  hoverColor: PropTypes.string,
  style: PropTypes.object
};

NeumorphicButtonShort.defaultProps = {
  type: 'button',
  className: '',
  textColor: 'white',
  hoverColor: '#9404C3',
  style: {}
};

NeumorphicButtonShort.displayName = 'NeumorphicButtonShort';

export default memo(NeumorphicButtonShort, arePropsEqual);